import type { MapTileLayer } from "./types";

export type ChartKey = "l1" | "l2" | "l3" | "l4" | "actual";

export type ChartConfig = {
  key: ChartKey;
  name: string;
  color: string; // #RRGGBB
};

export type MapActualBitrateColoring = {
  thresholdLowKbps: number;
  thresholdMidKbps: number;
  lowColor: string;
  midColor: string;
  highColor: string;
};

export type PersistedSettings = {
  delayOffsetMs: number;
  charts: ChartConfig[];
  followMap: boolean;
  showVideo: boolean;
  // trueのときサーバ側のUI向けダウンサンプルを無効化する（no_cap=1）。
  disableUiCap?: boolean;
  // trueのときグラフの画面側ダウンサンプルを無効化する。
  disableChartDownsample?: boolean;
  // trueのとき地図の画面側ダウンサンプルを無効化する。
  disableMapDownsample?: boolean;
  mapTileLayer: MapTileLayer;
  mapActualBitrateColoring: MapActualBitrateColoring;
  heatmapCellMeters?: number;
  heatmapBaseOpacity?: number;
  heatmapRenderMode?: "grid" | "smooth";
  // ヒートマップのスコア(0..1)に対する色の変化点（grid/smoothで描画特性が異なるため別管理）。
  heatmapGridScoreColorMid?: number;
  heatmapGridScoreColorLow?: number;
  heatmapSmoothScoreColorMid?: number;
  heatmapSmoothScoreColorLow?: number;
};

export type PersistedSettingsNullable = {
  delayOffsetMs: number | null;
  charts: ChartConfig[] | null;
  followMap: boolean | null;
  showVideo: boolean | null;
  disableUiCap: boolean | null;
  disableChartDownsample: boolean | null;
  disableMapDownsample: boolean | null;
  mapTileLayer: MapTileLayer | null;
  mapActualBitrateColoring: MapActualBitrateColoring | null;
  heatmapCellMeters: number | null;
  heatmapBaseOpacity: number | null;
  heatmapRenderMode: "grid" | "smooth" | null;
  heatmapGridScoreColorMid: number | null;
  heatmapGridScoreColorLow: number | null;
  heatmapSmoothScoreColorMid: number | null;
  heatmapSmoothScoreColorLow: number | null;
};

export const DEFAULT_NAME_BY_KEY: Record<ChartKey, string> = {
  l1: "L1 Bitrate",
  l2: "L2 Bitrate",
  l3: "L3 Bitrate",
  l4: "L4 Bitrate",
  actual: "Actual Bitrate",
};

export const BPSLOG_SETTINGS_STORAGE_KEY = "bpslog_settings_v2.0.2.1";

// #RRGGBB形式の文字列かどうかを判定する。
export const isHexColor = (v: unknown): v is string => {
  if (typeof v !== "string") return false;
  return /^#([0-9a-fA-F]{6})$/.test(v);
};

// #RRGGBBをrgba文字列へ変換する（不正値は黒として扱う）。
export const hexToRgba = (hex: string, alpha: number): string => {
  const m = /^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/.exec(hex);
  if (!m) return `rgba(0,0,0,${alpha})`;
  const r = parseInt(m[1], 16);
  const g = parseInt(m[2], 16);
  const b = parseInt(m[3], 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

const parseMapTileLayer = (v: unknown): MapTileLayer | null => {
  return v === "dark" || v === "osm" ? (v as MapTileLayer) : null;
};

const parseNumberOrNull = (v: unknown): number | null => {
  return typeof v === "number" && Number.isFinite(v) ? Math.trunc(v) : null;
};

const parseBooleanOrNull = (v: unknown): boolean | null => {
  return typeof v === "boolean" ? v : null;
};

const parseHeatmapCellMetersOrNull = (v: unknown): number | null => {
  if (typeof v !== "number" || !Number.isFinite(v)) return null;
  const n = Math.trunc(v);
  if (!Number.isFinite(n)) return null;
  return Math.max(5, Math.min(500, n));
};

const parseOpacity01OrNull = (v: unknown): number | null => {
  if (typeof v !== "number" || !Number.isFinite(v)) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(1, n));
};

const parseScore01OrNull = (v: unknown): number | null => {
  if (typeof v !== "number" || !Number.isFinite(v)) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(1, n));
};

const parseHeatmapRenderModeOrNull = (v: unknown): "grid" | "smooth" | null => {
  return v === "grid" || v === "smooth" ? v : null;
};

const parseMapActualBitrateColoring = (
  v: unknown,
  defaultColoring: MapActualBitrateColoring,
): MapActualBitrateColoring | null => {
  if (typeof v !== "object" || v === null) return null;
  const m = v as any;
  const thresholdLowKbps =
    typeof m.thresholdLowKbps === "number" &&
    Number.isFinite(m.thresholdLowKbps)
      ? Math.trunc(m.thresholdLowKbps)
      : defaultColoring.thresholdLowKbps;
  const thresholdMidKbps =
    typeof m.thresholdMidKbps === "number" &&
    Number.isFinite(m.thresholdMidKbps)
      ? Math.trunc(m.thresholdMidKbps)
      : defaultColoring.thresholdMidKbps;
  const lowColor = isHexColor(m.lowColor)
    ? m.lowColor
    : defaultColoring.lowColor;
  const midColor = isHexColor(m.midColor)
    ? m.midColor
    : defaultColoring.midColor;
  const highColor = isHexColor(m.highColor)
    ? m.highColor
    : defaultColoring.highColor;

  const safeLow = Math.max(0, thresholdLowKbps);
  const safeMid = Math.max(safeLow, Math.max(0, thresholdMidKbps));
  return {
    thresholdLowKbps: safeLow,
    thresholdMidKbps: safeMid,
    lowColor,
    midColor,
    highColor,
  };
};

export const loadPersistedSettings = (args: {
  storageKey?: string;
  defaultDelayOffsetMs: number;
  defaultCharts: ChartConfig[];
  defaultFollowMap: boolean;
  defaultShowVideo: boolean;
  defaultMapTileLayer: MapTileLayer;
  defaultMapActualBitrateColoring: MapActualBitrateColoring;
}): PersistedSettingsNullable => {
  // localStorageの保存値を読み、型を検証しつつ不足分はデフォルトで補う。
  const storageKey = args.storageKey ?? BPSLOG_SETTINGS_STORAGE_KEY;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return {
        delayOffsetMs: null,
        charts: null,
        followMap: null,
        showVideo: null,
        disableUiCap: null,
        disableChartDownsample: null,
        disableMapDownsample: null,
        mapTileLayer: null,
        mapActualBitrateColoring: null,
        heatmapCellMeters: null,
        heatmapBaseOpacity: null,
        heatmapRenderMode: null,
        heatmapGridScoreColorMid: null,
        heatmapGridScoreColorLow: null,
        heatmapSmoothScoreColorMid: null,
        heatmapSmoothScoreColorLow: null,
      };
    }
    const parsed = JSON.parse(raw);

    const delayOffsetMs = parseNumberOrNull(parsed?.delayOffsetMs);
    const followMap = parseBooleanOrNull(parsed?.followMap);
    const showVideo = parseBooleanOrNull(parsed?.showVideo);
    const disableChartDownsample = parseBooleanOrNull(
      parsed?.disableChartDownsample,
    );
    const disableMapDownsample = parseBooleanOrNull(
      parsed?.disableMapDownsample,
    );
    const disableUiCapRaw = parseBooleanOrNull(parsed?.disableUiCap);
    const disableUiCap =
      disableChartDownsample !== null || disableMapDownsample !== null
        ? Boolean(disableChartDownsample) || Boolean(disableMapDownsample)
        : disableUiCapRaw;
    const mapTileLayer = parseMapTileLayer(parsed?.mapTileLayer);
    const mapActualBitrateColoring = parseMapActualBitrateColoring(
      parsed?.mapActualBitrateColoring,
      args.defaultMapActualBitrateColoring,
    );
    const heatmapCellMeters = parseHeatmapCellMetersOrNull(
      parsed?.heatmapCellMeters,
    );
    const heatmapBaseOpacity = parseOpacity01OrNull(parsed?.heatmapBaseOpacity);
    const heatmapRenderMode = parseHeatmapRenderModeOrNull(
      parsed?.heatmapRenderMode,
    );
    const heatmapGridScoreColorMid = parseScore01OrNull(
      parsed?.heatmapGridScoreColorMid,
    );
    const heatmapGridScoreColorLow = parseScore01OrNull(
      parsed?.heatmapGridScoreColorLow,
    );
    const heatmapSmoothScoreColorMid = parseScore01OrNull(
      parsed?.heatmapSmoothScoreColorMid,
    );
    const heatmapSmoothScoreColorLow = parseScore01OrNull(
      parsed?.heatmapSmoothScoreColorLow,
    );

    const chartsRaw = parsed?.charts;
    if (!Array.isArray(chartsRaw)) {
      return {
        delayOffsetMs,
        charts: null,
        followMap,
        showVideo,
        disableUiCap,
        disableChartDownsample,
        disableMapDownsample,
        mapTileLayer,
        mapActualBitrateColoring,
        heatmapCellMeters,
        heatmapBaseOpacity,
        heatmapRenderMode,
        heatmapGridScoreColorMid,
        heatmapGridScoreColorLow,
        heatmapSmoothScoreColorMid,
        heatmapSmoothScoreColorLow,
      };
    }

    const nextCharts: ChartConfig[] = [];
    for (
      let i = 0;
      i < Math.min(chartsRaw.length, args.defaultCharts.length);
      i++
    ) {
      const c = chartsRaw[i];
      const rawKey = c?.key ?? c?.seriesKey;
      const key =
        rawKey === "l1" ||
        rawKey === "l2" ||
        rawKey === "l3" ||
        rawKey === "l4" ||
        rawKey === "actual"
          ? (rawKey as ChartKey)
          : null;
      const name = typeof c?.name === "string" ? c.name : "";
      const color = isHexColor(c?.color) ? c.color : null;
      if (!key || !color) {
        nextCharts.push({ ...args.defaultCharts[i] });
        continue;
      }
      nextCharts.push({
        key,
        name: name || args.defaultCharts[i].name,
        color,
      });
    }

    while (nextCharts.length < args.defaultCharts.length) {
      nextCharts.push({ ...args.defaultCharts[nextCharts.length] });
    }

    return {
      delayOffsetMs,
      charts: nextCharts,
      followMap,
      showVideo,
      disableUiCap,
      disableChartDownsample,
      disableMapDownsample,
      mapTileLayer,
      mapActualBitrateColoring,
      heatmapCellMeters,
      heatmapBaseOpacity,
      heatmapRenderMode,
      heatmapGridScoreColorMid,
      heatmapGridScoreColorLow,
      heatmapSmoothScoreColorMid,
      heatmapSmoothScoreColorLow,
    };
  } catch (e) {
    return {
      delayOffsetMs: null,
      charts: null,
      followMap: null,
      showVideo: null,
      disableUiCap: null,
      disableChartDownsample: null,
      disableMapDownsample: null,
      mapTileLayer: null,
      mapActualBitrateColoring: null,
      heatmapCellMeters: null,
      heatmapBaseOpacity: null,
      heatmapRenderMode: null,
      heatmapGridScoreColorMid: null,
      heatmapGridScoreColorLow: null,
      heatmapSmoothScoreColorMid: null,
      heatmapSmoothScoreColorLow: null,
    };
  }
};

export const persistSettings = (
  settings: PersistedSettings,
  storageKey: string = BPSLOG_SETTINGS_STORAGE_KEY,
) => {
  // 設定をlocalStorageへ保存する（失敗はUIを止めない）。
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(settings));
  } catch (e) {
    // 無視
  }
};
